import { Component, OnInit, Inject, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { API_BASE_URL } from '../../../environments/environment';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { StaffDetailDialogComponent } from '../../staffs/staff-detail-dialog/staff-detail-dialog.component';

// 開くときに渡すもの（どれも任意）
//   siteName・address：欠員募集から開くとき。物件名（物件だけ）と住所（清掃員・物件）を入れて、全部にチェックを付けて検索まで実行する
export interface DialogData {
  staffs?: any[];
  siteName?: string;
  address?: string;
}

// 清掃員の自宅のピン（場所・名前だけ）
export interface StaffPin {
  staffDailyCleaning: string;
  staff: string;
  latitude: string;
  longitude: string;
}
// 清掃員の雇用指示（今日の時点で終わっていないもの）
export interface StaffEmployment {
  sequenceNumber: string;
  cdSite: string;
  cdContract: string;
  startDay: string;
  endDay: string;
  notStarted: boolean;
  changed: boolean;
  workName: string;
  noteWork: string;
  mon: string; tue: string; wed: string; thu: string; fri: string; sat: string; sun: string;
  nameKubunHoliday: string;
  hasCarryingoutGarbage: string;
  hasExportingCompanysHoliday: string;
}
// 清掃員情報（ピンを押したときに取得する）
export interface Staff {
  staffDailyCleaning: string;
  staff: string;
  address1: string;
  address2: string;
  tel: string;
  siteData: string;
  siteInfo?: any[];
  employments?: StaffEmployment[];
}
// 契約コードの担当清掃員
export interface ContractStaff {
  staffDailyCleaning: string;
  nameStaff: string;
  tel: string;
  startDay: string;
  endDay: string;
  endKind: string;
  endPlanned: boolean;
  changed: boolean;
}
// 契約コードごとの状態と過不足（月＝xxxM、1週間あたり＝xxx2。足りないとマイナス）
// textMon などは曜日ごとの不足の文（「第4週 3h・第1,3,5週 1h」など。不足が無ければ null）
export interface Contract {
  cdSite: string;
  cdContract: string;
  state: string;
  day: string;
  startDateContract: string;
  endDateContract: string;
  notStarted: boolean;
  workName: string;
  staffCount: number;
  reason: string;
  staffs: ContractStaff[];
  totalM?: number;
  total2?: number;
  shortM?: number;
  siteTotalM?: number;
  siteTotal?: number;
}
// 物件のピン（場所・名前・状態だけ）
export interface SitePin {
  cdSite: string;
  state: string;
  site1: string;
  site2: string;
  longitude: string;
  latitude: string;
}
// 物件情報（ピンか一覧の行を押したときに取得する）
export interface Site {
  cdSite: string;
  state: string;
  site1: string;
  site2: string;
  address: string;
  client1: string;
  client2: string;
  day: string;
  contracts: Contract[];
}
// 手配が必要な契約の一覧の1行
export interface Need {
  cdSite: string;
  site1: string;
  site2: string;
  hasPosition: boolean;
  cdContract: string;
  state: string;
  reason: string;
  notStarted: boolean;
  staffCount: number;
  startDateContract: string;
  endDateContract: string;
  day: string;
  total2: number;
  totalM: number;
  shortM: number;
  siteTotal: number;
  siteTotalM: number;
}

// 地図に立てるピン
export interface Pin {
  key: string;
  // staff・now・future・covered・ok
  kind: string;
  id: string;
  title: string;
  lat: number;
  lng: number;
}

// この縮尺から先は、まとめずに1本ずつ出す
const NO_CLUSTER_ZOOM = 15;
// まとめる大きさ（画面のこの幅のマスに入るピンを1つにする）
const CLUSTER_CELL = 70;
// 1回に立てるピンの数（少しずつ立てて、画面が固まらないようにする）
const CHUNK = 100;
// 目立たせたいものを上にする
const Z_INDEX = { ok: 1, covered: 2, staff: 3, future: 4, now: 5 };

// 状態の強さ（1つの物件に複数当てはまるときは強いほうを出す）
const RANK = { now: 3, future: 2, covered: 1, ok: 0 };
// ピンの色
const COLOR = { staff: '#1f6fd6', now: '#d2332c', future: '#e08a00', covered: '#6a7587', ok: '#8e44ad' };

@Component({
  selector: 'app-map-dialog',
  templateUrl: './map-dialog.component.html',
  styleUrls: ['./map-dialog.component.less']
})

export class MapDialogComponent implements OnInit {

  // ローディング（検索中）
  nowLoading = false;
  // 下の内容を取得中
  detailLoading = false;

  // key：契約の時間帯、key + '2'：この契約の過不足（週）、m：同じく月、site：物件全体（週）、siteM：同じく月、text：不足の文
  weekList = [
    { key: 'mon', m: 'monM', site: 'siteMon', siteM: 'siteMonM', text: 'textMon', name: '月' },
    { key: 'tue', m: 'tueM', site: 'siteTue', siteM: 'siteTueM', text: 'textTue', name: '火' },
    { key: 'wed', m: 'wedM', site: 'siteWed', siteM: 'siteWedM', text: 'textWed', name: '水' },
    { key: 'thu', m: 'thuM', site: 'siteThu', siteM: 'siteThuM', text: 'textThu', name: '木' },
    { key: 'fri', m: 'friM', site: 'siteFri', siteM: 'siteFriM', text: 'textFri', name: '金' },
    { key: 'sat', m: 'satM', site: 'siteSat', siteM: 'siteSatM', text: 'textSat', name: '土' },
    { key: 'sun', m: 'sunM', site: 'siteSun', siteM: 'siteSunM', text: 'textSun', name: '日' }
  ];

  // ピンの色（チェックの横の絵にも使う）
  colors = COLOR;

  // 表示するもの（開いたときは全部外しておく。検索を押すまで取得しない）
  checks = { staff: false, now: false, future: false, covered: false, ok: false };
  // 物件のチェック
  siteChecks = [
    { key: 'now', name: '' },
    { key: 'future', name: '' },
    { key: 'covered', name: '担当なしだが、時間充足' },
    // 上の3つのどれにも当てはまらない物件
    { key: 'ok', name: '上記以外（問題なし）' }
  ];
  // 比べる日（M/D）
  dayNow = '';
  dayFuture = '';

  // 検索したときのチェック（件数はこれで出す）
  applied = null;

  // 建物名フィルター
  public siteName = "";
  // 清掃員名フィルター
  public staffName = "";
  // 住所フィルター
  public address = "";

  // 検索対象（その条件を清掃員・物件のどちらに効かせるか）
  // 物件名は物件だけ、清掃員名は清掃員だけ、住所は両方が最初の状態
  targets = {
    siteName: { staff: false, site: true },
    staffName: { staff: true, site: false },
    address: { staff: true, site: true }
  };

  // 検索欄の下に出す文
  message = '';
  isWarn = false;

  // ピン
  staffs: StaffPin[] = [];
  sites: SitePin[] = [];
  // 手配が必要な契約の一覧（あるときだけ右に出す）
  rows: Need[] = [];
  // 件数
  counts = { staff: 0, now: 0, future: 0, covered: 0, ok: 0 };

  // 選んだもの（内容は押したときに取得する）
  selectedKey: string = null;
  selectedStaff: Staff = null;
  selectedSite: Site = null;
  selectedContract: string = null;

  // ピンにするもの（検索で取得したもの全部。key は 'staff' + 清掃員コード、'site' + 物件コード）
  pins: Pin[] = [];
  // 立てたマーカー（地図に見えていて、まとめられていないものだけ作る）
  markers = {};
  // 近いピンをまとめたマーカー
  clusters = [];
  // 描き直しの番号（描いている途中で地図が動いたら、古いほうをやめる）
  renderSeq = 0;

  map = null
  markerLib = null

  public headers = new HttpHeaders({
    "X-LoginAccessKey": localStorage.getItem("accessKey")
  });

  constructor(private http: HttpClient, private router: Router, @Inject(MAT_DIALOG_DATA) private data: DialogData, private dialogRef: MatDialogRef<MapDialogComponent>, private ngZone: NgZone, private dialog: MatDialog) { }

  async ngOnInit() {
    // 比べる日は2つ（今日の1か月後と2か月後。今日が 10/21 なら 11/21 と 12/21）。1か月後は欠員募集と同じ日なので、×の物件＝欠員募集の「不足あり」の物件
    const today = new Date();
    this.dayNow = this.monthsLater(today, 1);
    this.dayFuture = this.monthsLater(today, 2);
    this.siteChecks[0].name = '1か月後（' + this.dayNow + 'に）時間不足';
    this.siteChecks[1].name = '2か月後（' + this.dayFuture + 'に）時間不足';

    // 地図だけ先に出す（データは検索を押してから）
    const { maps } = (window as any).google;
    this.markerLib = await maps.importLibrary("marker");
    this.map = new maps.Map(document.getElementById("map") as HTMLElement, {
      zoom: 10,
      center: { lat: 35.6894, lng: 139.6917 },    // 東京
      mapId: 'akbs4504f8b37365c3d0',
    });

    // 地図を動かす・縮尺を変えるたびに、見えている範囲のピンを立て直す
    this.map.addListener('idle', () => this.renderPins());

    // 欠員募集から「近くの清掃員を探す」で開いたとき：物件名と住所を入れて、全部にチェックを付けて検索まで実行する
    if (this.data && (this.data.siteName || this.data.address)) {
      this.siteName = this.data.siteName || '';
      this.address = this.data.address || '';
      this.checks = { staff: true, now: true, future: true, covered: true, ok: true };
      this.search();
    }
  }

  /**
   * 何か月後の同じ日（M/D）。バックの CURRENT_DATE + make_interval(months) と同じ数え方（月末を越える日は月末に寄せる。1/31 の1か月後は 2/28）
   * @param base 基準の日
   * @param add 何か月後か
   */
  monthsLater(base: Date, add: number): string {
    let d = new Date(base.getFullYear(), base.getMonth() + add, base.getDate());
    // 日が変わっていたら月末を越えている（例：1/31 → 3/3）ので、その月の末日（翌月の 0 日）にする
    if (d.getDate() != base.getDate()) {
      d = new Date(base.getFullYear(), base.getMonth() + add + 1, 0);
    }
    return (d.getMonth() + 1) + '/' + d.getDate();
  }

  goTo(comp, param){
    this.ngZone.run(()=>{
    this.router.navigate([comp, param])
    });
  }

  /**
   * 検索 ボタン イベント
   * チェックを付けたものだけ取得する。取得するのはピンと一覧の行だけ（内容は押したときに取得する）
   */
  async search() {
    const c = this.checks;
    if (!c.staff && !c.now && !c.future && !c.covered && !c.ok) {
      this.isWarn = true;
      this.message = '表示するものを1つ以上選んでください。';
      return;
    }

    // 条件を入れたのに検索対象が1つも無いと、その条件が効かないので知らせる
    const noTarget = [
      { value: this.siteName, target: this.targets.siteName, name: '物件名' },
      { value: this.staffName, target: this.targets.staffName, name: '清掃員名' },
      { value: this.address, target: this.targets.address, name: '住所' }
    ].filter(f => f.value.trim() && !f.target.staff && !f.target.site);
    if (noTarget.length) {
      this.isWarn = true;
      this.message = '「' + noTarget[0].name + '」の検索対象を1つ以上選んでください。';
      return;
    }

    this.nowLoading = true;
    this.isWarn = false;
    this.message = '';

    const applied = { staff: c.staff, now: c.now, future: c.future, covered: c.covered, ok: c.ok };

    await this.http.get(
      API_BASE_URL + "/api/v1/map?"
        + "&staffName=" + encodeURIComponent(this.staffName)
        + "&siteName=" + encodeURIComponent(this.siteName)
        + "&address=" + encodeURIComponent(this.address)
        + "&siteNameStaff=" + this.targets.siteName.staff
        + "&siteNameSite=" + this.targets.siteName.site
        + "&staffNameStaff=" + this.targets.staffName.staff
        + "&staffNameSite=" + this.targets.staffName.site
        + "&addressStaff=" + this.targets.address.staff
        + "&addressSite=" + this.targets.address.site
        + "&staff=" + applied.staff
        + "&now=" + applied.now
        + "&future=" + applied.future
        + "&covered=" + applied.covered
        + "&ok=" + applied.ok
        , {headers: this.headers})
          .toPromise().then((res: any) => {
        this.applied = applied;
        this.staffs = res.staffs || [];
        this.sites = res.sites || [];
        this.rows = res.needs || [];
        this.setCounts();
        this.selectedKey = null;
        this.selectedStaff = null;
        this.selectedSite = null;
        this.selectedContract = null;
        this.drawingMarkers();
        if (!this.staffs.length && !this.sites.length) {
          this.message = 'チェックや検索条件を変えて、検索を押してください。';
        }
      })
      .catch((error: any) => {
        alert("認証に失敗しました");
        this.router.navigate(['/']);
      }
    );

    this.nowLoading = false;
  }

  /**
   * 清掃員の担当物件を整理
   */
  setStaffSiteInfo(staff: Staff) {
    const row = []
    if (staff.siteData) {
      const tar = staff.siteData.replace('{', '').replace('}', '')
      const tar2 = tar.split(',')
      for (let i = 0; i < tar2.length; i++) {
        const s = tar2[i]
        if (s.indexOf('@@') > -1) {
          const val = s.replace(/"/g, '').split('@@')
          if (val && val.length) {
            // その物件の雇用指示（曜日ごとの勤務。改行は空白にする）
            const employments = (staff.employments || []).filter(e => e.cdSite == val[0]);
            employments.forEach(e => {
              this.weekList.forEach(w => {
                if (e[w.key]) e[w.key] = String(e[w.key]).replace(/\n/g, ' ');
              });
            });
            row.push({ cdSite: val[0], site1: val[1] || '', site2: val[2] || '', address: val[3] || '', client1: val[4] || '', client2: val[5] || '', employments: employments })
          }
        }
      }
    }
    staff.siteInfo = row
  }

  /**
   * 雇用指示の勤務がある曜日
   */
  employmentDays(e: StaffEmployment) {
    return this.weekList.filter(w => e[w.key]);
  }

  /**
   * 件数
   */
  setCounts() {
    const counts = { staff: this.staffs.length, now: 0, future: 0, covered: 0, ok: 0 };
    this.sites.forEach(site => {
      counts[site.state]++;
    })
    this.counts = counts;
  }

  /**
   * ピンの絵
   * @param kind staff・now・future・covered・ok
   */
  pinElement(kind: string): HTMLElement {
    const el = document.createElement('div');
    el.style.cursor = 'pointer';
    el.style.transformOrigin = '50% 100%';
    const color = COLOR[kind];
    if (kind == 'staff') {
      // 青い丸に人の形
      el.innerHTML = '<svg width="30" height="30" viewBox="-17 -17 34 34" xmlns="http://www.w3.org/2000/svg">'
        + '<circle r="15" cx="0" cy="0" fill="' + color + '" stroke="#ffffff" stroke-width="2"></circle>'
        + '<circle r="4.6" cx="0" cy="-4.5" fill="#ffffff"></circle>'
        + '<path d="M-8.5 9.5 C-8.5 2.5 8.5 2.5 8.5 9.5 Z" fill="#ffffff"></path></svg>';
      return el;
    }
    let glyph = '<circle r="4.5" cx="0" cy="-28" fill="#ffffff"></circle>';
    if (kind == 'now') {
      glyph = '<path d="M-6 -34 L6 -22 M6 -34 L-6 -22" stroke="#ffffff" stroke-width="3.4" stroke-linecap="round" fill="none"></path>';
    } else if (kind == 'future') {
      glyph = '<path d="M0 -36 L7.5 -22.5 L-7.5 -22.5 Z" fill="#ffffff"></path>';
    }
    el.innerHTML = '<svg width="30" height="40" viewBox="-17 -45 34 47" xmlns="http://www.w3.org/2000/svg">'
      + '<path d="M0 0 C-5 -12 -15 -17 -15 -28 A15 15 0 1 1 15 -28 C15 -17 5 -12 0 0 Z" fill="' + color + '" stroke="#ffffff" stroke-width="2"></path>'
      + glyph + '</svg>';
    return el;
  }

  /**
   * マーカー描画（前のマーカーは消す）
   * ここではピンにするものを覚えるだけ。実際に立てるのは renderPins
   */
  drawingMarkers() {
    Object.keys(this.markers).forEach(key => {
      this.markers[key].map = null;
    })
    this.markers = {};

    const pins: Pin[] = [];
    this.sites.forEach(site => {
      if (!site.latitude) return;
      pins.push({
        key: 'site' + site.cdSite, kind: site.state, id: site.cdSite,
        title: (site.site1 || '') + (site.site2 || ''),
        lat: Number(site.latitude), lng: Number(site.longitude)
      });
    })
    this.staffs.forEach(staff => {
      if (!staff.latitude) return;
      pins.push({
        key: 'staff' + staff.staffDailyCleaning, kind: 'staff', id: staff.staffDailyCleaning,
        title: staff.staff,
        lat: Number(staff.latitude), lng: Number(staff.longitude)
      });
    })
    this.pins = pins;

    this.renderPins();
  }

  /**
   * 地図に見えている範囲のピンを立てる
   * 近いピンは1つにまとめて件数を出す（清掃員と物件は別々にまとめる）。寄ると1本ずつに分かれる
   * 選んでいるピンは、まとめずに必ず1本で出す
   */
  renderPins() {
    const seq = ++this.renderSeq;

    this.clusters.forEach(c => c.map = null);
    this.clusters = [];

    const bounds = this.map ? this.map.getBounds() : null;
    if (!bounds) return;
    const zoom = this.map.getZoom();

    // マスごとに分ける
    const groups = {};
    const singles: Pin[] = [];
    this.pins.forEach(pin => {
      if (!bounds.contains({ lat: pin.lat, lng: pin.lng })) return;
      if (zoom >= NO_CLUSTER_ZOOM || pin.key == this.selectedKey) {
        singles.push(pin);
        return;
      }
      const cell = this.cellOf(pin, zoom) + (pin.kind == 'staff' ? ':staff' : ':site');
      if (!groups[cell]) groups[cell] = [];
      groups[cell].push(pin);
    })

    const clusters = [];
    Object.keys(groups).forEach(cell => {
      if (groups[cell].length == 1) {
        singles.push(groups[cell][0]);
      } else {
        clusters.push(groups[cell]);
      }
    })

    // 出さなくなったピンを地図から外す（作ったマーカーは取っておいて使い回す）
    const show = {};
    singles.forEach(pin => show[pin.key] = true);
    Object.keys(this.markers).forEach(key => {
      if (!show[key] && this.markers[key].map) this.markers[key].map = null;
    })

    // まとめたピン
    clusters.forEach(list => this.clusters.push(this.clusterMarker(list, zoom)));

    // 1本ずつのピンは、少しずつ立てる
    let index = 0;
    const step = () => {
      // 途中で地図が動いたら、新しいほうに任せる
      if (seq != this.renderSeq) return;
      const end = Math.min(index + CHUNK, singles.length);
      for (; index < end; index++) {
        this.showMarker(singles[index]);
      }
      if (index < singles.length) setTimeout(step, 0);
    }
    step();
  }

  /**
   * ピンが入るマス（その縮尺での画面上の位置を、決まった幅で区切る）
   */
  cellOf(pin: Pin, zoom: number): string {
    const scale = 256 * Math.pow(2, zoom);
    const x = (pin.lng + 180) / 360 * scale;
    const sin = Math.sin(pin.lat * Math.PI / 180);
    const y = (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale;
    return Math.floor(x / CLUSTER_CELL) + ':' + Math.floor(y / CLUSTER_CELL);
  }

  /**
   * 1本のピンを地図に出す（まだ作っていなければ作る）
   */
  showMarker(pin: Pin) {
    let marker = this.markers[pin.key];
    if (!marker) {
      const { AdvancedMarkerElement } = this.markerLib;
      marker = new AdvancedMarkerElement({
        title: pin.title,
        position: { lat: pin.lat, lng: pin.lng },
        content: this.pinElement(pin.kind),
        zIndex: Z_INDEX[pin.kind],
        gmpClickable: true,
      });
      marker.addListener('click', () => {
        this.ngZone.run(() => {
          if (pin.kind == 'staff') {
            this.selectStaff(pin.id);
          } else {
            this.selectSite(pin.id, null, null, false);
          }
        });
      })
      this.markers[pin.key] = marker;
    }
    if (!marker.map) marker.map = this.map;
    this.applyHighlight(pin.key);
  }

  /**
   * まとめたピン（件数を出す丸）
   * 色は、中のピンのうち一番強い状態の色。押すと、その場所へ寄る
   */
  clusterMarker(list: Pin[], zoom: number) {
    let kind = list[0].kind;
    let lat = 0;
    let lng = 0;
    list.forEach(pin => {
      if (Z_INDEX[pin.kind] > Z_INDEX[kind]) kind = pin.kind;
      lat += pin.lat;
      lng += pin.lng;
    })
    const position = { lat: lat / list.length, lng: lng / list.length };

    const size = list.length < 10 ? 34 : (list.length < 100 ? 40 : 46);
    const el = document.createElement('div');
    el.style.cursor = 'pointer';
    el.style.width = size + 'px';
    el.style.height = size + 'px';
    el.style.lineHeight = (size - 6) + 'px';
    el.style.borderRadius = '50%';
    el.style.border = '3px solid #ffffff';
    el.style.boxSizing = 'border-box';
    el.style.boxShadow = '0 1px 4px rgba(0, 0, 0, 0.4)';
    el.style.backgroundColor = COLOR[kind];
    el.style.color = '#ffffff';
    el.style.fontSize = '13px';
    el.style.fontWeight = 'bold';
    el.style.textAlign = 'center';
    // 丸の真ん中が場所に来るようにする（何もしないと丸の下の端が場所に来る）
    el.style.transform = 'translateY(50%)';
    el.textContent = String(list.length);

    const { AdvancedMarkerElement } = this.markerLib;
    const marker = new AdvancedMarkerElement({
      map: this.map,
      title: (kind == 'staff' ? '清掃員 ' : '物件 ') + list.length + ' 件（押すと拡大）',
      position: position,
      content: el,
      zIndex: Z_INDEX[kind],
      gmpClickable: true,
    });
    marker.addListener('click', () => {
      this.map.setCenter(position);
      this.map.setZoom(zoom + 2);
    })
    return marker;
  }

  /**
   * 選んだピンを大きくする
   * @param key マーカーの key
   */
  highlight(key: string) {
    const before = this.selectedKey;
    this.selectedKey = key;
    // 前に選んでいたピンを元の大きさに戻す
    if (before && before != key) this.applyHighlight(before);
    // まとめられていたピンを1本で出す
    this.renderPins();
  }

  /**
   * ピンの大きさを、選んでいるかどうかに合わせる
   */
  applyHighlight(key: string) {
    const marker = this.markers[key];
    if (!marker) return;
    const el = marker.content as HTMLElement;
    const pin = this.pins.filter(p => p.key == key)[0];
    if (key == this.selectedKey) {
      el.style.transform = 'scale(1.5)';
      marker.zIndex = 10;
    } else {
      el.style.transform = '';
      marker.zIndex = pin ? Z_INDEX[pin.kind] : 1;
    }
  }

  /**
   * 清掃員を選ぶ（内容を取得して下に出す）
   * @param staffDailyCleaning 清掃員コード
   */
  selectStaff(staffDailyCleaning: string) {
    const key = 'staff' + staffDailyCleaning;
    this.selectedStaff = null;
    this.selectedSite = null;
    this.selectedContract = null;
    this.highlight(key);

    this.detailLoading = true;
    this.http.get(API_BASE_URL + "/api/v1/map/staff?staffDailyCleaning=" + encodeURIComponent(staffDailyCleaning),
      {headers: this.headers}).subscribe((res: any) => {
        // 待っている間に別のものを選んでいたら出さない
        if (this.selectedKey != key) return;
        if (res) {
          this.setStaffSiteInfo(res);
          this.selectedStaff = res;
        }
        this.detailLoading = false;
      },
      error => {
        alert("認証に失敗しました");
        this.router.navigate(['/']);
    });
  }

  /**
   * 物件を選ぶ（内容を取得して下に出す）
   * @param cdSite 物件コード
   * @param cdContract 一覧から選んだときの契約コード
   * @param day 一覧から選んだときの、その行の比べた日（表はこの日の時点で出す）
   * @param move 地図をその物件へ動かすなら true
   */
  selectSite(cdSite: string, cdContract: string, day: string, move: boolean) {
    const key = 'site' + cdSite;
    this.selectedStaff = null;
    this.selectedSite = null;
    this.selectedContract = cdContract;
    this.highlight(key);

    // マーカーはまだ作っていないことがあるので、場所はピンの元のデータから取る
    const pin = this.pins.filter(p => p.key == key)[0];
    if (move && pin) {
      this.map.panTo({ lat: pin.lat, lng: pin.lng });
    }

    this.detailLoading = true;
    this.http.get(API_BASE_URL + "/api/v1/map/site?cdSite=" + encodeURIComponent(cdSite)
        + (day ? "&day=" + encodeURIComponent(day) : ""),
      {headers: this.headers}).subscribe((res: any) => {
        // 待っている間に別のものを選んでいたら出さない
        if (this.selectedKey != key) return;
        if (res) {
          this.selectedSite = res;
        }
        this.detailLoading = false;
      },
      error => {
        alert("認証に失敗しました");
        this.router.navigate(['/']);
    });
  }

  /**
   * 清掃員名クリックイベント
   * 清掃員詳細ダイアログを開く（清掃員一覧で名前を押したときと同じ）
   * @param staffDailyCleaning 清掃員コード
   */
  staffDetailDialogOpen(staffDailyCleaning: string) {
    this.dialog.open(StaffDetailDialogComponent, {
      data: {
        data: { staffDailyCleaning: staffDailyCleaning }
      }
    });
  }

  /**
   * 物件全体の過不足を持っている行（どの契約の行にも同じ値が入っている）
   */
  siteContract(site: Site): Contract {
    return site.contracts[0];
  }

  /**
   * 選んだ物件の契約（契約コードの昇順）
   */
  sortedContracts(site: Site): Contract[] {
    return site.contracts.slice().sort((a, b) => {
      return a.cdContract < b.cdContract ? -1 : (a.cdContract > b.cdContract ? 1 : 0);
    })
  }

  /**
   * 状態の説明
   * @param c 契約、または一覧の行
   */
  stateLabel(c): string {
    const noStaff = !Number(c.staffCount);
    if (c.state == 'now') {
      return noStaff ? '担当なし・1か月後、時間も不足' : '担当はいるが、1か月後、時間が不足';
    }
    if (c.state == 'future') {
      if (noStaff && c.notStarted) return '担当なし・これから始まる契約';
      return noStaff ? '担当なし・2か月後、時間も不足' : '担当はいるが、2か月後、時間が不足';
    }
    if (c.state == 'covered') {
      return '担当なしだが、物件の時間は充足';
    }
    return '時間充足で問題なし';
  }

  /**
   * 担当がいるか（比べた日の時点）
   * @param c 契約、または一覧の行
   */
  hasStaff(c): boolean {
    return Number(c.staffCount) > 0;
  }

  /**
   * 時間の状態の説明（一覧用。担当がいるかは札で出すので、ここには入れない）
   * @param c 一覧の行
   */
  timeLabel(c): string {
    const noStaff = !this.hasStaff(c);
    if (c.state == 'now') {
      return noStaff ? '1か月後、物件の時間も不足' : '1か月後、物件の時間が不足';
    }
    if (c.state == 'future') {
      if (noStaff && c.notStarted) return 'これから始まる契約';
      return noStaff ? '2か月後、物件の時間も不足' : '2か月後、物件の時間が不足';
    }
    return '物件の時間は充足';
  }

  /**
   * 状態の印
   */
  stateMark(state: string): string {
    if (state == 'now') return '×';
    if (state == 'future') return '▲';
    return '●';
  }

  /**
   * 過不足時間の表示
   * 不足は「-」が付くので、超過は「+」を付けて向きが分かるようにする
   */
  signedHours(value, unit: string = 'h'): string {
    const hours = Number(value) || 0;
    // 小数2桁まで。末尾の 0 は出さない
    const text = String(parseFloat(Math.abs(hours).toFixed(2)));
    return (hours > 0 ? '+' + text : (hours < 0 ? '−' + text : '0')) + ' ' + unit;
  }

  /**
   * 余りを回したあとの不足が、この契約の過不足と違う（同じ物件のほかの契約の余りで変わった）ときだけ true
   * 同じ数字なら2か所に出さない
   */
  shortDiffers(c: Contract): boolean {
    const shortM = Number(c.shortM) || 0;
    if (shortM <= 0) return false;
    return Math.abs(shortM + (Number(c.totalM) || 0)) > 0.001;
  }

  /**
   * 過不足時間のクラス
   */
  hoursClass(value): string {
    const hours = Number(value) || 0;
    if (hours < 0) return 'minus';
    if (hours > 0) return 'plus';
    return 'zero';
  }

  /**
   * この契約で足りない曜日
   * @param c 契約、または一覧の行
   */
  shortDays(c) {
    // 不足の文がある曜日（同じ物件の余りを回したあとで足りない曜日）
    return this.weekList.filter(w => c[w.text]);
  }

  /**
   * 担当清掃員の予定（これから入る・辞める）
   */
  staffNote(s: ContractStaff): string {
    const notes = [];
    if (s.startDay) notes.push(s.startDay + ' から' + (s.changed ? '勤務変更' : ''));
    if (s.endDay) {
      // 勤務変更で終わる雇用指示は、辞めるわけではないので「まで」とだけ出す
      notes.push(s.endKind == '勤務変更' ? s.endDay + ' まで' : s.endDay + ' ' + s.endKind + (s.endPlanned ? '予定' : ''));
    }
    return notes.length ? '（' + notes.join('、') + '）' : '';
  }

  /**
   * 閉じる ボタン イベント
   */
  close() {
    this.dialogRef.close();
  }
}
